import { noop } from 'lodash';
import { useMutation } from 'react-query';

import { NEW_VIEW } from '@/constants/dashboardConstants';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';
import { ProjectTemplate } from '@experiment-management-shared/types';
import { projectViewsStore } from '@shared/utils';
import useProject from './useProject';

// @todo: deduplicate with `useUpsertViewMutation`
const getLocalViewId = (view: ProjectTemplate) => {
  if (view.v3?.isPersonalView) {
    return NEW_VIEW;
  }

  return view.templateId ?? view.createdFromTemplateId ?? NEW_VIEW;
};

const prepareViewToUpsert = ({
  view,
  userName
}: {
  view: ProjectTemplate;
  userName: string;
}): { templateId: string } & ProjectTemplate => {
  return {
    ...view,
    templateId: getLocalViewId(view),
    lastUpdate: new Date().getTime(),
    v3: {
      ...view.v3,
      lastUpdatedBy: userName
    }
  };
};

const upsertView = async ({
  projectId,
  view,
  userName
}: {
  projectId: string;
  view: ProjectTemplate;
  userName: string;
}) => {
  const preparedView = prepareViewToUpsert({ view, userName });

  await projectViewsStore
    .put({
      projectId,
      view: preparedView,
      viewId: preparedView.templateId
    })
    .catch(noop);

  return preparedView;
};

export default function useUpsertLocalViewMutation() {
  const { data: project } = useProject();
  const { user: userData } = useCurrentUserContext();
  const userName = userData?.username || '';
  const projectId = project?.projectId;

  return useMutation(({ view }: { view: ProjectTemplate }) => {
    return upsertView({ projectId, view, userName });
  });
}
